import { cx } from '@amumin/ui-core';

import styles from './RadioList.module.css';

export interface RadioListOption<T extends string = string> {
  value: T;
  label: string;
  /** Eén regel uitleg, bv. "12 afspraken worden aangepast". */
  hint?: string;
}

export interface RadioListProps<T extends string = string> {
  /** Naam van de radiogroep (form-naam én toegankelijke naam via `label`). */
  name: string;
  label: string;
  options: ReadonlyArray<RadioListOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Legenda alleen voor schermlezers (als de dialoogtitel dezelfde vraag al stelt). */
  hideLabel?: boolean;
  /**
   * De aanroeper heeft zelf al een fieldset met dezelfde vraag als legend (bv. met uitleg eronder):
   * dan geen tweede fieldset en legend, anders leest een schermlezer de vraag twee keer voor.
   */
  inFieldset?: boolean;
}

/**
 * Grote keuzeregels (hele rij tikbaar). Voor de bereikvraag bij herhalingen: "Alleen deze keer /
 * Deze en volgende / De hele reeks" (ONTWERP.md §5.3, §7.4) en andere expliciete keuzes.
 */
export function RadioList<T extends string>({
  name,
  label,
  options,
  value,
  onChange,
  hideLabel,
  inFieldset,
}: RadioListProps<T>) {
  const rows = options.map((o) => {
    const id = `${name}-${o.value}`;
    const checked = o.value === value;
    return (
      <label key={o.value} class={cx(styles.option, checked && styles.checked)} for={id}>
        <input
          id={id}
          class={styles.radio}
          type="radio"
          name={name}
          value={o.value}
          checked={checked}
          onChange={() => onChange(o.value)}
        />
        <span class={styles.text}>
          <span class={styles.label}>{o.label}</span>
          {o.hint ? <span class={styles.hint}>{o.hint}</span> : null}
        </span>
      </label>
    );
  });
  if (inFieldset) return <div class={styles.list}>{rows}</div>;
  return (
    <fieldset class={styles.list}>
      <legend class={hideLabel ? 'fb-sr-only' : styles.legend}>{label}</legend>
      {rows}
    </fieldset>
  );
}
